// @ts-strict-ignore
/**
 * @fileoverview FeatureFlagProvider goes here
 */
import React, { useEffect, useReducer } from "react";

import * as braze from "@braze/web-sdk-develop";
import type { FeatureFlag } from "@braze/web-sdk-develop";

import type { FeatureFlagMap } from "@lib/featureFlags";
import { FeatureFlagListContext, defaultFeatureFlagDefinitions } from "@lib/featureFlags";
import { getFeatureFlag } from "@lib/featureFlags/getFeatureFlag";
import { addFeatureFlagListener, removeFeatureFlagListener } from "@lib/initBrazeAtBraze";

import type { FeatureFlagProviderProps } from "./types";

/** detect differences between the last feature flag and the next one */
const diff = (
  newProps: Record<string, string | boolean | number>,
  oldProps: Record<string, string | boolean | number>
): boolean => {
  const newEntries = Object.entries(newProps);
  const oldKeys = Object.keys(oldProps);

  if (newEntries.length !== oldKeys.length) {
    return true;
  }
  return newEntries.some(([key, value]) => oldProps[key] !== value);
};

export const FeatureFlagProvider = ({ children }: FeatureFlagProviderProps): React.ReactElement => {
  const [definitions, updateFlags] = useReducer(
    (definitions: FeatureFlagMap, flags: FeatureFlag[]) => {
      // we will store changed feature flags here
      const changes: Partial<FeatureFlagMap> = {};
      flags.forEach((flag) => {
        const newFlag = getFeatureFlag(flag);
        const oldFlag = definitions[flag.id];
        if (!oldFlag) {
          changes[flag.id] = newFlag;
          return;
        }
        if (oldFlag.enabled !== newFlag.enabled || diff(oldFlag.properties, newFlag.properties)) {
          changes[flag.id] = newFlag;
        }
      });
      if (!Object.keys(changes).length) {
        // no changes, don't re-render
        return definitions;
      }
      // replace the old flag definitions with the new ones. Doing it this way will prevent re-rendering UI based on unchanged flags
      return { ...definitions, ...changes };
    },
    {
      ...defaultFeatureFlagDefinitions,
      ...Object.fromEntries(
        braze.getAllFeatureFlags()?.map((flag) => {
          const newFlag = getFeatureFlag(flag);
          return [flag.id, newFlag];
        }) || []
      ),
    }
  );

  useEffect(() => {
    // subscribe to feature flag updates
    addFeatureFlagListener(updateFlags);
    return () => removeFeatureFlagListener(updateFlags);
  }, []);

  return <FeatureFlagListContext.Provider value={definitions}>{children}</FeatureFlagListContext.Provider>;
};
